import {
  cloneElement,
  isValidElement,
  useCallback,
  useMemo,
  type ComponentType,
  type ReactElement,
  type ReactNode,
} from "react";
import { Navigate, Outlet } from "react-router-dom";
import { canAccess, canAccessGlobal, type Need } from "@shared/access/check";
import type { Level } from "@shared/access/levels";
import { isBelow, type Place } from "@shared/access/places";
import type { ResourcePath } from "@shared/access/resources";
import { browserAccess, type BrowserAccess } from "@shared/access/snapshot";
import type { AuthMeResponse } from "@shared/types/auth";
import { useOptionalAuth } from "./AuthContext";
import { useScope } from "../context/ScopeContext";

/** A place as the CMS names it: the wire `premiseId`, a company id, Nenda, or global items. */
export type PlaceRef =
  | "nenda"
  | "global"
  | { readonly premiseId: string }
  | { readonly companyId: string };

export type Needs = Need | readonly Need[];

/** What a page needs, and where it is asked: the page's place, Nenda, or global items. */
export interface PageAccess {
  readonly needs: Needs;
  readonly place: "page" | "nenda" | "global";
}

export type GateAccess = BrowserAccess;

export function accessOf(user: AuthMeResponse | null): GateAccess | null {
  if (!user?.access) return null;
  return browserAccess(user.access);
}

/** A premise the snapshot does not name sits under Nenda, so only a Nenda grant reaches it. */
function resolve(
  access: BrowserAccess,
  ref: Exclude<PlaceRef, "global">
): Place {
  if (ref === "nenda") return access.nenda;
  if ("premiseId" in ref) {
    return (
      access.premiseBySlug(ref.premiseId) ?? {
        kind: "premise",
        id: `unknown:${ref.premiseId}`,
      }
    );
  }
  return { kind: "company", id: ref.companyId };
}

/** The gate's answer: the check at that place. */
export function allows(
  access: GateAccess | null,
  needs: Needs,
  ref: PlaceRef
): boolean {
  if (!access) return false;
  if (ref === "global") {
    return canAccessGlobal(access.user, needs, access.nenda, access.tree);
  }
  return canAccess(access.user, needs, resolve(access, ref), access.tree);
}

/**
 * For menus and page routes: yes at the place, or at a covered place under it,
 * so a customer with no location picked still sees their premises' pages.
 */
export function allowsAtOrBelow(
  access: GateAccess | null,
  needs: Needs,
  ref: PlaceRef
): boolean {
  if (!access) return false;
  if (ref === "global") return allows(access, needs, ref);
  const place = resolve(access, ref);
  const { user, tree } = access;
  if (canAccess(user, needs, place, tree)) return true;
  return access.covered.some(
    (p) => isBelow(p, place, tree) && canAccess(user, needs, p, tree)
  );
}

function useBrowserAccess(): GateAccess | null {
  const user = useOptionalAuth()?.user ?? null;
  return useMemo(() => accessOf(user), [user]);
}

/** The place the page is on: the scoped premise, else the scoped company, else Nenda. */
export function usePagePlace(): PlaceRef {
  const { premiseId, companyId } = useScope().scope;
  return useMemo(() => {
    if (premiseId) return { premiseId };
    if (companyId) return { companyId };
    return "nenda";
  }, [premiseId, companyId]);
}

/** The user's home company, else Nenda: where routes that scope by the home company are checked. */
export function useHomePlace(): PlaceRef {
  const company = useOptionalAuth()?.user?.access?.homeCompanyId ?? null;
  return useMemo(() => (company ? { companyId: company } : "nenda"), [company]);
}

/** An item's company, or Nenda for an item with none. */
export function companyPlace(companyId: string | null | undefined): PlaceRef {
  return companyId ? { companyId } : "nenda";
}

/** Yes or no, the same answer as `PermissionGate` with the same props. */
export function usePermission(
  resource: ResourcePath,
  level: Level,
  place?: PlaceRef
): boolean {
  const access = useBrowserAccess();
  const page = usePagePlace();
  return allows(access, { resource, level }, place ?? page);
}

export function usePageAccess(): (page: PageAccess) => boolean {
  const access = useBrowserAccess();
  const here = usePagePlace();
  return useCallback(
    (page: PageAccess) =>
      page.place === "page"
        ? allowsAtOrBelow(access, page.needs, here)
        : allows(access, page.needs, page.place),
    [access, here]
  );
}

export interface PermissionGateProps {
  resource: ResourcePath;
  level: Level;
  /** An item from above passes its own place. Defaults to the page's place. */
  place?: PlaceRef;
  /** On a no, render the child with these props instead of hiding it. */
  deniedProps?: Record<string, unknown>;
  /** On a no, render this instead of the child. */
  DeniedComponent?: ComponentType;
  children: ReactNode;
}

export function PermissionGate({
  resource,
  level,
  place,
  deniedProps,
  DeniedComponent,
  children,
}: PermissionGateProps) {
  if (usePermission(resource, level, place)) return <>{children}</>;
  if (DeniedComponent) return <DeniedComponent />;
  if (deniedProps && isValidElement(children)) {
    return cloneElement(children as ReactElement<Record<string, unknown>>, {
      ...deniedProps,
    });
  }
  return null;
}

/** Ungated, so a refusal never redirects back into another refusal. */
export const REFUSED_PATH = "/premises";

/** Layout route: renders the page only when the user may open it. */
export function RequireAccess({ page }: { page: PageAccess }) {
  return usePageAccess()(page) ? (
    <Outlet />
  ) : (
    <Navigate to={REFUSED_PATH} replace />
  );
}
